import React, { useState, useEffect } from "react";

const AnimalGame = () => {
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(3);
  const [currentQuestion, setCurrentQuestion] = useState({});
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [celebration, setCelebration] = useState(false);
  const [gameMode, setGameMode] = useState("count"); // 'count', 'identify', 'sound'

  const animals = {
    farm: { emoji: "🐄", name: "Cow", sound: "Moo!" },
    pig: { emoji: "🐷", name: "Pig", sound: "Oink!" },
    sheep: { emoji: "🐑", name: "Sheep", sound: "Baa!" },
    horse: { emoji: "🐴", name: "Horse", sound: "Neigh!" },
    duck: { emoji: "🦆", name: "Duck", sound: "Quack!" },
    chicken: { emoji: "🐔", name: "Chicken", sound: "Cluck!" },
    cat: { emoji: "🐱", name: "Cat", sound: "Meow!" },
    dog: { emoji: "🐶", name: "Dog", sound: "Woof!" },
    lion: { emoji: "🦁", name: "Lion", sound: "Roar!" },
    elephant: { emoji: "🐘", name: "Elephant", sound: "Trumpet!" },
    monkey: { emoji: "🐵", name: "Monkey", sound: "Ooh Ooh!" },
    bear: { emoji: "🐻", name: "Bear", sound: "Growl!" },
    tiger: { emoji: "🐯", name: "Tiger", sound: "Growl!" },
    giraffe: { emoji: "🦒", name: "Giraffe", sound: "Bleat!" },
    zebra: { emoji: "🦓", name: "Zebra", sound: "Whinny!" },
    panda: { emoji: "🐼", name: "Panda", sound: "Bleat!" },
    koala: { emoji: "🐨", name: "Koala", sound: "Bellow!" },
    kangaroo: { emoji: "🦘", name: "Kangaroo", sound: "Chortle!" },
    fox: { emoji: "🦊", name: "Fox", sound: "Yelp!" },
    rabbit: { emoji: "🐰", name: "Rabbit", sound: "Squeak!" },
    mouse: { emoji: "🐭", name: "Mouse", sound: "Squeak!" },
    whale: { emoji: "🐋", name: "Whale", sound: "Sing!" },
    dolphin: { emoji: "🐬", name: "Dolphin", sound: "Click!" },
    shark: { emoji: "🦈", name: "Shark", sound: "Silent!" },
    octopus: { emoji: "🐙", name: "Octopus", sound: "Squirt!" },
    penguin: { emoji: "🐧", name: "Penguin", sound: "Honk!" },
    owl: { emoji: "🦉", name: "Owl", sound: "Hoot!" },
    eagle: { emoji: "🦅", name: "Eagle", sound: "Screech!" },
    parrot: { emoji: "🦜", name: "Parrot", sound: "Squawk!" },
    frog: { emoji: "🐸", name: "Frog", sound: "Ribbit!" },
    turtle: { emoji: "🐢", name: "Turtle", sound: "Hiss!" },
    snake: { emoji: "🐍", name: "Snake", sound: "Hiss!" },
    crocodile: { emoji: "🐊", name: "Crocodile", sound: "Bellow!" },
    rhino: { emoji: "🦏", name: "Rhino", sound: "Bellow!" },
    hippo: { emoji: "🦛", name: "Hippo", sound: "Grunts!" },
    bat: { emoji: "🦇", name: "Bat", sound: "Screech!" },
    unicorn: { emoji: "🦄", name: "Unicorn", sound: "Neigh!" },
    dinosaur: { emoji: "🦕", name: "Dinosaur", sound: "Roar!" },

    // ➕ New Animals
    camel: { emoji: "🐪", name: "Camel", sound: "Grumble!" },
    donkey: { emoji: "🐴", name: "Donkey", sound: "Hee-haw!" },
    goose: { emoji: "🦢", name: "Goose", sound: "Honk!" },
    turkey: { emoji: "🦃", name: "Turkey", sound: "Gobble!" },
    lobster: { emoji: "🦞", name: "Lobster", sound: "Click!" },
    crab: { emoji: "🦀", name: "Crab", sound: "Clack!" },
    squid: { emoji: "🦑", name: "Squid", sound: "Whoosh!" },
    seal: { emoji: "🦭", name: "Seal", sound: "Arf!" },
    hedgehog: { emoji: "🦔", name: "Hedgehog", sound: "Snuffle!" },
    squirrel: { emoji: "🐿️", name: "Squirrel", sound: "Chatter!" },
    wolf: { emoji: "🐺", name: "Wolf", sound: "Howl!" },
    buffalo: { emoji: "🐃", name: "Buffalo", sound: "Snort!" },
    bison: { emoji: "🐂", name: "Bison", sound: "Snort!" },
    llama: { emoji: "🦙", name: "Llama", sound: "Hum!" },
    sloth: { emoji: "🦥", name: "Sloth", sound: "Murmur!" },
    orangutan: { emoji: "🦧", name: "Orangutan", sound: "Hoot!" },
    insect: { emoji: "🐞", name: "Ladybug", sound: "Buzz!" },
    bee: { emoji: "🐝", name: "Bee", sound: "Buzz!" },
    butterfly: { emoji: "🦋", name: "Butterfly", sound: "Flutter!" },
    ant: { emoji: "🐜", name: "Ant", sound: "March!" },
  };

  const animalKeys = Object.keys(animals);

  useEffect(() => {
    generateQuestion();
  }, [gameMode, level]);

  // Auto-advance to next question after answer
  useEffect(() => {
    if (selectedAnswer !== null) {
      const timer = setTimeout(() => {
        if (!gameOver) {
          nextQuestion();
        }
      }, 1500); // 1.5 second delay before auto-advancing

      return () => clearTimeout(timer);
    }
  }, [selectedAnswer, gameOver]);

  const generateQuestion = () => {
    setSelectedAnswer(null);
    setFeedback("");
    setShowNext(false);
    setCelebration(false);

    if (gameMode === "count") {
      generateCountQuestion();
    } else if (gameMode === "identify") {
      generateIdentifyQuestion();
    } else if (gameMode === "sound") {
      generateSoundQuestion();
    }
  };

  const generateCountQuestion = () => {
    const maxCount = Math.min(4 + Math.floor(level / 2), 8);
    const count = Math.floor(Math.random() * maxCount) + 1;
    const selectedAnimalKey =
      animalKeys[Math.floor(Math.random() * animalKeys.length)];
    const selectedAnimal = animals[selectedAnimalKey];

    const question = {
      type: "count",
      count,
      animal: selectedAnimal,
      display: Array(count).fill(selectedAnimal.emoji),
      question: `How many ${selectedAnimal.name.toLowerCase()}s do you see?`,
      correctAnswer: count,
    };

    setCurrentQuestion(question);
    generateCountOptions(count);
  };

  const generateIdentifyQuestion = () => {
    const selectedAnimalKey =
      animalKeys[Math.floor(Math.random() * animalKeys.length)];
    const selectedAnimal = animals[selectedAnimalKey];

    const question = {
      type: "identify",
      animal: selectedAnimal,
      display: [selectedAnimal.emoji],
      question: `What animal is this?`,
      correctAnswer: selectedAnimal.name,
    };

    setCurrentQuestion(question);
    generateIdentifyOptions(selectedAnimal.name);
  };

  const generateSoundQuestion = () => {
    const selectedAnimalKey =
      animalKeys[Math.floor(Math.random() * animalKeys.length)];
    const selectedAnimal = animals[selectedAnimalKey];

    const question = {
      type: "sound",
      animal: selectedAnimal,
      display: [selectedAnimal.emoji],
      question: `What sound does "${selectedAnimal.name}" make?`,
      correctAnswer: selectedAnimal.sound,
    };

    setCurrentQuestion(question);
    generateSoundOptions(selectedAnimal.sound);
  };

  const generateCountOptions = (correctAnswer) => {
    const optionsSet = new Set([correctAnswer]);
    const maxOption = Math.max(8, correctAnswer + 3);

    while (optionsSet.size < 4) {
      const option = Math.floor(Math.random() * maxOption) + 1;
      optionsSet.add(option);
    }

    const optionsArray = Array.from(optionsSet).sort(() => Math.random() - 0.5);
    setOptions(optionsArray);
  };

  const generateIdentifyOptions = (correctAnswer) => {
    const optionsSet = new Set([correctAnswer]);

    while (optionsSet.size < 4) {
      const randomAnimalKey =
        animalKeys[Math.floor(Math.random() * animalKeys.length)];
      optionsSet.add(animals[randomAnimalKey].name);
    }

    const optionsArray = Array.from(optionsSet).sort(() => Math.random() - 0.5);
    setOptions(optionsArray);
  };

  const generateSoundOptions = (correctAnswer) => {
    const optionsSet = new Set([correctAnswer]);

    while (optionsSet.size < 4) {
      const randomAnimalKey =
        animalKeys[Math.floor(Math.random() * animalKeys.length)];
      optionsSet.add(animals[randomAnimalKey].sound);
    }

    const optionsArray = Array.from(optionsSet).sort(() => Math.random() - 0.5);
    setOptions(optionsArray);
  };

  const checkAnswer = (answer) => {
    if (selectedAnswer !== null) return;

    setSelectedAnswer(answer);

    if (answer === currentQuestion.correctAnswer) {
      setScore((prev) => prev + 10 * level);
      setFeedback("🎉 Excellent! Well done!");
      setCelebration(true);

      if (score > 0 && (score + 10 * level) % 100 === 0) {
        setLevel((prev) => prev + 1);
      }
    } else {
      setFeedback(
        `❌ Oops! The correct answer was ${currentQuestion.correctAnswer}`
      );
      setLives((prev) => prev - 1);

      if (lives <= 1) {
        setGameOver(true);
        return;
      }
    }

    setShowNext(true);
  };

  const nextQuestion = () => {
    generateQuestion();
  };

  const restartGame = () => {
    setScore(0);
    setLevel(1);
    setLives(3);
    setGameOver(false);
    generateQuestion();
  };

  if (gameOver) {
    return (
      <>
        

        <div className="game-container">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-12 col-md-8 col-lg-6">
                <div className="game-area">
                  <div className="text-center p-4">
                    <h2 className="game-over-title mb-3">🎮 Game Over! 🎮</h2>
                    <p className="fs-4 mb-2">
                      Final Score: <strong>{score}</strong>
                    </p>
                    <p className="fs-4 mb-4">
                      Level Reached: <strong>{level}</strong>
                    </p>
                    <button onClick={restartGame} className="btn restart-btn">
                      🚀 Play Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>
          {`
            .game-container {
              min-height: 100vh;
              background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
              padding: 20px;
              font-family: 'Comic Sans MS', cursive, sans-serif;
            }
            
            .game-area {
              background: rgba(255, 255, 255, 0.95);
              border-radius: 20px;
              box-shadow: 0 8px 25px rgba(0,0,0,0.2);
            }
            
            .game-over-title {
              color: #4a5568;
              font-size: 2rem;
            }
            
            .restart-btn {
              padding: 15px 30px;
              border: none;
              border-radius: 25px;
              background: linear-gradient(45deg, #56ab2f, #a8e6cf);
              color: white;
              font-size: 1.2rem;
              font-weight: bold;
              cursor: pointer;
              box-shadow: 0 4px 15px rgba(0,0,0,0.2);
              transition: all 0.3s ease;
            }
            
            .restart-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(0,0,0,0.3);
            }
          `}
        </style>
      </>
    );
  }

  return (
    <>
      <link
        href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.2/css/bootstrap.min.css"
        rel="stylesheet"
      />

      <div className="">
        <div className="">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-8">
              <div className="game-area p-4">
                {/* Header */}
                <div className="text-center mb-4">
                  <h1 className="game-title">🐾 Animal Adventure 🐾</h1>

                  {/* Mode Buttons */}
                  <div className="d-flex justify-content-center gap-2 mb-3 mode-buttons-container">
                    <button
                      onClick={() => setGameMode("count")}
                      className={`btn mode-btn ${
                        gameMode === "count" ? "active" : ""
                      }`}
                    >
                      <div>🔢</div>
                      <div>Count</div>
                    </button>
                    <button
                      onClick={() => setGameMode("identify")}
                      className={`btn mode-btn ${
                        gameMode === "identify" ? "active" : ""
                      }`}
                    >
                      <div>🔍</div>
                      <div>Identify</div>
                    </button>
                    <button
                      onClick={() => setGameMode("sound")}
                      className={`btn mode-btn ${
                        gameMode === "sound" ? "active" : ""
                      }`}
                    >
                      <div>🔊</div>
                      <div>Sounds</div>
                    </button>
                  </div>

                  {/* Score Board */}
                  <div className="d-flex justify-content-center flex-wrap gap-3 score-board">
                    <div className="score-item">Score: {score}</div>
                    <div className="score-item">Level: {level}</div>
                    <div className="score-item">
                      Lives: {"❤️".repeat(lives)}
                    </div>
                  </div>
                </div>

                {/* Question */}
                <div className="question-text text-center mb-4">
                  {currentQuestion.question}
                </div>

                {/* Animal Display */}
                <div className="animal-display d-flex justify-content-center flex-wrap gap-3 my-4 align-items-center">
                  {currentQuestion.display?.map((animal, index) => (
                    <div
                      key={index}
                      className="animal-emoji"
                      style={{ animationDelay: `${index * 0.2}s` }}
                    >
                      {animal}
                    </div>
                  ))}
                </div>

                {/* Options Grid */}
                <div
                  className={`options-grid ${
                    currentQuestion.type === "count"
                      ? "count-grid"
                      : "text-grid"
                  } mx-auto mb-4`}
                >
                  {options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => checkAnswer(option)}
                      className={`btn option-btn ${
                        currentQuestion.type === "count"
                          ? "count-btn"
                          : "text-btn"
                      } 
                        ${
                          selectedAnswer === option &&
                          option === currentQuestion.correctAnswer
                            ? "correct"
                            : ""
                        }
                        ${
                          selectedAnswer === option &&
                          option !== currentQuestion.correctAnswer
                            ? "wrong"
                            : ""
                        }
                        ${
                          selectedAnswer !== null &&
                          option === currentQuestion.correctAnswer &&
                          selectedAnswer !== option
                            ? "correct"
                            : ""
                        }
                      `}
                      disabled={selectedAnswer !== null}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                {/* Feedback */}
                <div
                  className={`feedback text-center mb-2 ${
                    feedback.includes("Excellent") ? "success" : "error"
                  }`}
                >
                  {feedback}
                </div>

                {/* Auto-advance indicator */}
                {selectedAnswer !== null && (
                  <div className="text-center">
                    <div className="auto-advance-indicator">
                      Next question in 1.5 seconds...
                    </div>
                  </div>
                )}

                {/* Celebration Effect */}
                {celebration && <div className="celebration">🎉</div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          /* Base Styles */
          .game-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
            padding: 10px;
            font-family: 'Comic Sans MS', cursive, sans-serif;
          }
          
          .game-area {
            background: rgba(255, 255, 255, 0.95);
            border-radius: 15px;
            // box-shadow: 0 6px 20px rgba(0,0,0,0.15);
          }
          
          /* Typography */
          .game-title {
            font-size: clamp(1.3rem, 4vw, 2.2rem);
            margin-bottom: 10px;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
            color: #4a5568;
            margin-top: -25px;
          }
          
          .question-text {
            font-size: clamp(1.5rem, 3vw, 1.6rem);
            color: #2d3748;
            font-weight: bold;
            line-height: 1.3;
          }
          
          /* Mode Buttons */
          .mode-buttons-container {
            flex-wrap: nowrap !important;
            justify-content: center;
            align-items: center;
            width: 100%;
            padding: 0 5px;
            gap: 8px;
          }
          
          .mode-btn {
            border: none;
            border-radius: 12px;
            font-size: clamp(0.65rem, 2vw, 0.85rem);
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s ease;
            background: linear-gradient(45deg, #ffecd2, #fcb69f);
            color: #2d3748;
            flex: 1;
            aspect-ratio: 1;
            max-width: 90px;
            min-width: 70px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 6px;
            line-height: 1.1;
          }
          
          .mode-btn.active {
            background: linear-gradient(45deg, #56ab2f, #a8e6cf);
            color: white;
            transform: scale(1.05);
          }
          
          .mode-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 3px 10px rgba(0,0,0,0.2);
          }
          
          /* Score Items */
          .score-item {
            background: linear-gradient(45deg, #ff9a9e, #fecfef);
            color: #2d3748;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: clamp(0.8rem, 2vw, 1rem);
            font-weight: bold;
          }
          
          /* Animal Display */
          .animal-display {
            min-height: 60px;
          }
          
          .animal-emoji {
            font-size: clamp(2.5rem, 7vw, 3.5rem);
            animation: bounce 2s infinite;
            margin: 3px;
          }
          
          /* Options Grid */
          .options-grid {
            display: grid;
            gap: 10px;
          }
          
          .count-grid {
            grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
            max-width: 200px;
          }
          
          .text-grid {
            grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
            max-width: 500px;
          }
          
          /* Option Buttons */
          .option-btn {
            border: none;
            border-radius: 15px;
            font-weight: bold;
            cursor: pointer;
            background: linear-gradient(45deg, #ffecd2, #fcb69f);
            color: #2d3748;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 3px 12px rgba(0,0,0,0.15);
            padding: 8px;
          }
          
          .count-btn {
            aspect-ratio: 1;
            font-size: clamp(1.3rem, 4vw, 1.8rem);
            min-height: 70px;
          }
          
          .text-btn {
            font-size: clamp(0.9rem, 3vw, 1.2rem);
            min-height: 50px;
            padding: 8px 12px;
          }
          
          .option-btn:hover:not(:disabled) {
            transform: translateY(-3px);
            box-shadow: 0 6px 20px rgba(0,0,0,0.3);
          }
          
          .option-btn.correct {
            background: linear-gradient(45deg, #56ab2f, #a8e6cf);
            color: white;
            transform: scale(1.1);
            animation: pulse 0.6s ease;
          }
          
          .option-btn.wrong {
            background: linear-gradient(45deg, #ff6b6b, #ff8e8e);
            color: white;
            animation: shake 0.5s ease;
          }
          
          .option-btn:disabled {
            cursor: not-allowed;
          }
          
          /* Feedback */
          .feedback {
            font-size: clamp(1rem, 3vw, 1.3rem);
            font-weight: bold;
            min-height: 25px;
          }
          
          .feedback.success {
            color: #56ab2f;
          }
          
          .feedback.error {
            color: #ff6b6b;
          }
          
          /* Auto-advance indicator */
          .auto-advance-indicator {
            font-size: clamp(0.8rem, 2vw, 1rem);
            color: #666;
            font-style: italic;
            margin-top: 10px;
          }
          
          /* Celebration Effect */
          .celebration {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: clamp(3rem, 10vw, 4rem);
            z-index: 1000;
            animation: celebrate 1s ease-in-out;
            pointer-events: none;
          }
          
          /* Animations */
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { 
              transform: translateY(0); 
            }
            40% { 
              transform: translateY(-10px); 
            }
            60% { 
              transform: translateY(-5px); 
            }
          }
          
          @keyframes pulse {
            0% { 
              transform: scale(1.1); 
            }
            50% { 
              transform: scale(1.3); 
            }
            100% { 
              transform: scale(1.1); 
            }
          }
          
          @keyframes shake {
            0%, 100% { 
              transform: translateX(0); 
            }
            25% { 
              transform: translateX(-5px); 
            }
            75% { 
              transform: translateX(5px); 
            }
          }
          
          @keyframes celebrate {
            0% { 
              transform: translate(-50%, -50%) scale(0); 
              opacity: 0; 
            }
            50% { 
              transform: translate(-50%, -50%) scale(1.2); 
              opacity: 1; 
            }
            100% { 
              transform: translate(-50%, -50%) scale(1); 
              opacity: 0; 
            }
          }
          
          /* Responsive Design */
          @media (max-width: 576px) {
            .options-grid {
              gap: 10px;
            }
            
            .count-grid {
              grid-template-columns: repeat(2, 1fr);
            }
            
            .text-grid {
              grid-template-columns: 1fr;
            }
            
            .animal-display {
              gap: 10px;
            }
            
            .score-item {
              padding: 8px 16px;
              font-size: 0.9rem;
            }
             
            .score-board{
            gap: 3px!important;
            flex-wrap: nowrap !important;
            }

            .mode-buttons-container {
              padding: 0 5px;
              gap: 8px;
            }
            
            .mode-btn {
              font-size: 0.65rem;
              max-width: 85px;
              min-width: 70px;
              border-radius: 12px;
              padding: 6px;
            }
          }
          
          @media (max-width: 380px) {
            .mode-buttons-container {
              gap: 5px;
            }
            
            .mode-btn {
              font-size: 0.6rem;
              min-width: 60px;
              max-width: 75px;
              border-radius: 10px;
              padding: 4px;
            }
          }
          
          /* Accessibility */
          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }
          
          /* Focus styles for accessibility */
          .btn:focus,
          .option-btn:focus,
          .mode-btn:focus {
            outline: 2px solid #0066cc;
            outline-offset: 2px;
          }
        `}
      </style>
    </>
  );
};

export default AnimalGame;
